import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BriefcaseBusiness, CircleUserRound, FilePlus2, FolderKanban } from "lucide-react";
import config from "../../config/config";
import CockpitShell from "../../components/CockpitShell";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const navItems = [
  { href: "/dashboard/employer", label: "Overview", icon: BriefcaseBusiness },
  { href: "/dashboard/stb/create", label: "Neue Anzeige", icon: FilePlus2 },
  { href: "/dashboard/stb/manage", label: "Verwalten", icon: FolderKanban },
  { href: "/dashboard/account", label: "Account", icon: CircleUserRound },
];

const statuses = ["all", "draft", "published", "active", "paused", "expired", "archived"];
const sortOptions = [
  { value: "updated_desc", label: "Zuletzt aktualisiert" },
  { value: "created_desc", label: "Neueste zuerst" },
  { value: "created_asc", label: "Aelteste zuerst" },
  { value: "title_asc", label: "Titel A-Z" },
  { value: "title_desc", label: "Titel Z-A" },
];

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getAvailableActions(status) {
  const normalized = String(status || "").toLowerCase();
  const actions = ["preview", "edit", "duplicate", "applications"];

  if (normalized === "draft") {
    actions.push("publish", "archive", "delete");
    return actions;
  }
  if (normalized === "published") {
    actions.push("resume", "pause", "archive");
    return actions;
  }
  if (normalized === "active") {
    actions.push("pause", "archive");
    return actions;
  }
  if (normalized === "paused") {
    actions.push("resume", "archive");
    return actions;
  }
  if (normalized === "expired") {
    actions.push("publish", "archive");
    return actions;
  }

  return actions;
}

const StbAdvManage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [queryApplied, setQueryApplied] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("updated_desc");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [data, setData] = useState({ total: 0, rows: [] });

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setQueryApplied(query.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const params = useMemo(
    () => ({
      q: queryApplied || undefined,
      status: status === "all" ? undefined : status,
      sort,
      page,
      limit,
    }),
    [queryApplied, status, sort, page, limit]
  );

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${config.API_BASE_URL}/api/stb/employer/ads`, { params, headers: authHeaders() });
      setData({ total: res.data?.total || 0, rows: res.data?.rows || [] });
    } catch (e) {
      setMessage(e.response?.data?.message || "Anzeigen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [params]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / limit));

  const doAction = async (ad, action) => {
    try {
      if (action === "preview") navigate(`/dashboard/stb/preview/${ad.id}`);
      if (action === "edit") navigate(`/dashboard/stb/create?id=${ad.id}`);
      if (action === "duplicate") await axios.post(`${config.API_BASE_URL}/api/stb/employer/ads/${ad.id}/duplicate`, {}, { headers: authHeaders() });
      if (action === "publish") await axios.post(`${config.API_BASE_URL}/api/stb/employer/ads/${ad.id}/publish`, {}, { headers: authHeaders() });
      if (action === "pause") {
        if (!window.confirm("Anzeige pausieren?")) return;
        await axios.post(`${config.API_BASE_URL}/api/stb/employer/ads/${ad.id}/pause`, {}, { headers: authHeaders() });
      }
      if (action === "resume") await axios.post(`${config.API_BASE_URL}/api/stb/employer/ads/${ad.id}/resume`, {}, { headers: authHeaders() });
      if (action === "archive") {
        if (!window.confirm("Anzeige archivieren?")) return;
        await axios.post(`${config.API_BASE_URL}/api/stb/employer/ads/${ad.id}/archive`, {}, { headers: authHeaders() });
      }
      if (action === "delete") {
        if (!window.confirm("Draft wirklich loeschen?")) return;
        await axios.delete(`${config.API_BASE_URL}/api/stb/employer/ads/${ad.id}`, { headers: authHeaders() });
      }
      if (action === "applications") navigate(`/dashboard/stb/applications?adId=${ad.id}`);
      setMessage(`Action "${action}" erfolgreich.`);
      await load();
    } catch (e) {
      setMessage(e.response?.data?.message || `Action "${action}" fehlgeschlagen.`);
    }
  };

  return (
    <CockpitShell
      title="Anzeigenverwaltung"
      subtitle="Verwalten bleibt Teil desselben Hiring Control Decks mit breiter Hauptflaeche statt schmaler Admin-Saeule."
      badge="Employer Ad Management"
      navItems={navItems}
      wideLayout
      theme="employer"
      shellLabel="Manage Ads"
      shellDescription="Folgeflaeche im Arbeitgeber-Template fuer Lifecycle, Filter und Bewerbungszugang."
      modeLabel="Manage Flow"
      liveLabel="Employer"
      topSignal="Employer System"
      topContext="Anzeige verwalten"
      actions={
        <Button className="dashboard-action-button" onClick={() => navigate("/dashboard/stb/create")}>
          Neue Anzeige
        </Button>
      }
    >
      {message ? <p className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-700">{message}</p> : null}

      <Card className="dashboard-card-shell">
        <CardHeader><CardTitle className="dashboard-card-title">Filter</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-6">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Suche Titel/Beruf/Ort" className="dashboard-field md:col-span-2" />
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="dashboard-field"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sort} onValueChange={(v) => { setSort(v); setPage(1); }}>
            <SelectTrigger className="dashboard-field"><SelectValue placeholder="Sortierung" /></SelectTrigger>
            <SelectContent>{sortOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
            <SelectTrigger className="dashboard-field"><SelectValue placeholder="Page size" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="12">12 / Seite</SelectItem>
              <SelectItem value="24">24 / Seite</SelectItem>
              <SelectItem value="50">50 / Seite</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="dashboard-inline-button" onClick={() => {
            setQuery("");
            setQueryApplied("");
            setStatus("all");
            setSort("updated_desc");
            setPage(1);
            setMessage("");
          }}>
            Zuruecksetzen
          </Button>
        </CardContent>
      </Card>

      <Card className="dashboard-card-shell">
        <CardHeader>
          <CardTitle className="dashboard-card-title">Anzeigen ({data.total || 0})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-40 animate-pulse rounded-xl border border-slate-200 bg-slate-100" />
              ))}
            </div>
          ) : null}

          {!loading && data.rows.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-600">
              <p className="text-lg text-slate-900">Keine Anzeigen gefunden.</p>
              <p className="mt-1 text-sm">Erstelle eine neue Anzeige oder passe den Filter an.</p>
              <Button className="mt-3 dashboard-action-button" onClick={() => navigate("/dashboard/stb/create")}>
                Neue Anzeige
              </Button>
            </div>
          ) : null}

          {!loading && data.rows.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {data.rows.map((ad) => (
                <Card key={ad.id} className="dashboard-card-shell">
                  <CardContent className="space-y-3 pt-4 text-sm text-slate-600">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs text-slate-400">{ad.beruf_text || "-"}</p>
                        <p className="font-semibold text-slate-900">{ad.titel || "-"}</p>
                      </div>
                      <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">{ad.status}</Badge>
                    </div>
                    <p className="text-xs text-slate-400">{ad.standort_ort || "-"} · {ad.arbeitsform}</p>
                    <p className="line-clamp-3 text-slate-600">{ad.beschreibung || "-"}</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-slate-400">ID #{ad.id}</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" className="dashboard-inline-button">...</Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {getAvailableActions(ad.status).includes("preview") ? <DropdownMenuItem onClick={() => doAction(ad, "preview")}>View / Preview</DropdownMenuItem> : null}
                          {getAvailableActions(ad.status).includes("edit") ? <DropdownMenuItem onClick={() => doAction(ad, "edit")}>Edit</DropdownMenuItem> : null}
                          {getAvailableActions(ad.status).includes("duplicate") ? <DropdownMenuItem onClick={() => doAction(ad, "duplicate")}>Duplicate</DropdownMenuItem> : null}
                          {getAvailableActions(ad.status).includes("publish") ? <DropdownMenuItem onClick={() => doAction(ad, "publish")}>Publish</DropdownMenuItem> : null}
                          {getAvailableActions(ad.status).includes("pause") ? <DropdownMenuItem onClick={() => doAction(ad, "pause")}>Deactivate (Pause)</DropdownMenuItem> : null}
                          {getAvailableActions(ad.status).includes("resume") ? <DropdownMenuItem onClick={() => doAction(ad, "resume")}>{ad.status === "published" ? "Activate" : "Resume"}</DropdownMenuItem> : null}
                          {getAvailableActions(ad.status).includes("archive") ? <DropdownMenuItem onClick={() => doAction(ad, "archive")}>Archive</DropdownMenuItem> : null}
                          {getAvailableActions(ad.status).includes("delete") ? <DropdownMenuItem onClick={() => doAction(ad, "delete")}>Delete Draft</DropdownMenuItem> : null}
                          {getAvailableActions(ad.status).includes("applications") ? <DropdownMenuItem onClick={() => doAction(ad, "applications")}>Bewerbungen</DropdownMenuItem> : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : null}

          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>Seite {page} / {totalPages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="dashboard-inline-button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                Prev
              </Button>
              <Button size="sm" variant="outline" className="dashboard-inline-button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="dashboard-card-shell">
        <CardHeader><CardTitle className="dashboard-card-title">Bewerbungen</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-600">
          <p>Die leane Bewerbungs- und Candidate-Sicht laeuft pro Anzeige ueber den fuehrenden Employer-Ads-Pfad.</p>
        </CardContent>
      </Card>
    </CockpitShell>
  );
};

export default StbAdvManage;
